import { supabase } from '../lib/supabase';

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

export const quotationService = {
  /**
   * Get quotation by work_order_id. Returns null if not found.
   */
  async getByWorkOrderId(workOrderId) {
    try {
      const { data, error } = await supabase?.from('quotations')?.select('*, sale_note:sale_notes(*)')?.eq('work_order_id', workOrderId)?.maybeSingle();

      if (error) {
        console.error('Error fetching quotation:', error);
        return null;
      }
      return data ? toCamelCase(data) : null;
    } catch (err) {
      console.error('Failed to fetch quotation:', err);
      return null;
    }
  },

  /**
   * Ensure a draft quotation exists for a work order.
   */
  async ensureDraftForWorkOrder(workOrderId, saleNoteId = null, companyId = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();

      const { data: existing } = await supabase?.from('quotations')?.select('*')?.eq('work_order_id', workOrderId)?.maybeSingle();

      if (existing) return toCamelCase(existing);

      // Resolve company_id and folio from work_order
      const { data: wo } = await supabase?.from('work_orders')?.select('company_id, folio_ot')?.eq('id', workOrderId)?.maybeSingle();
      const resolvedCompanyId = companyId || wo?.company_id || null;
      const folioOt = wo?.folio_ot || workOrderId?.slice(0, 8);
      const folioCot = 'COT-' + folioOt?.replace('OT-', '');

      const { data: created, error: createErr } = await supabase?.from('quotations')?.insert({
          work_order_id: workOrderId,
          company_id: resolvedCompanyId,
          sale_note_id: saleNoteId,
          folio_cotizacion: folioCot,
          status: 'draft',
          created_by: user?.id || null
        })?.select()?.single();

      if (createErr) {
        console.error('Error creating quotation draft:', createErr);
        return null;
      }
      return toCamelCase(created);
    } catch (err) {
      console.error('Failed to ensure quotation draft:', err);
      return null;
    }
  },

  async update(id, updateData) {
    try {
      const snakeData = toSnakeCase(updateData);
      const { data, error } = await supabase?.from('quotations')?.update(snakeData)?.eq('id', id)?.select()?.single();

      if (error) throw error;
      return toCamelCase(data);
    } catch (err) {
      console.error('Failed to update quotation:', err);
      throw err;
    }
  }
};

export const saleNoteService = {
  /**
   * Get sale note by work_order_id. Returns null if not found.
   */
  async getByWorkOrderId(workOrderId) {
    if (!workOrderId) return null;
    try {
      const { data, error } = await supabase?.from('sale_notes')?.select('*, items:sale_note_items(*)')?.eq('work_order_id', workOrderId)?.maybeSingle();

      if (error) {
        console.error('[saleNoteService] Error fetching sale note:', error);
        return null;
      }
      return data ? toCamelCase(data) : null;
    } catch (err) {
      console.error('[saleNoteService] Failed to fetch sale note:', err);
      return null;
    }
  },

  /**
   * Ensure a draft sale note exists for a work order.
   * Fixed: resolvedCompanyId and folioNota are now always resolved before insert.
   */
  async ensureDraftForWorkOrder(workOrderId, companyId = null) {
    if (!workOrderId) {
      console.error('[saleNoteService] ensureDraftForWorkOrder: workOrderId is required');
      return null;
    }
    try {
      const { data: { user } } = await supabase?.auth?.getUser();

      // Check if sale_note already exists
      const { data: existing } = await supabase?.from('sale_notes')?.select('*')?.eq('work_order_id', workOrderId)?.maybeSingle();
      if (existing) {
        console.log('[saleNoteService] Existing sale note found:', existing?.id);
        // Ensure quotation also exists
        await quotationService?.ensureDraftForWorkOrder(workOrderId, existing?.id, companyId);
        return toCamelCase(existing);
      }

      // Resolve company_id and folio from work_order
      const { data: wo, error: woErr } = await supabase?.from('work_orders')?.select('company_id, folio_ot')?.eq('id', workOrderId)?.maybeSingle();
      if (woErr) {
        console.error('[saleNoteService] Error fetching work order:', woErr);
      }
      const resolvedCompanyId = companyId || wo?.company_id || null;
      const folioOt = wo?.folio_ot || workOrderId?.slice(0, 8);
      const folioNota = 'NV-' + (folioOt?.replace('OT-', '') || Date.now()?.toString(36)?.toUpperCase());

      console.log('[saleNoteService] Creating sale note for OT:', folioOt, 'company:', resolvedCompanyId);

      const { data: created, error: createErr } = await supabase?.from('sale_notes')?.insert({
        work_order_id: workOrderId,
        company_id: resolvedCompanyId,
        folio_nota: folioNota,
        status: 'draft',
        created_by: user?.id || null
      })?.select()?.single();

      if (createErr) {
        console.error('[saleNoteService] Error creating sale note:', createErr);
        return null;
      }

      const saleNote = toCamelCase(created);
      // Ensure quotation draft also exists
      await quotationService?.ensureDraftForWorkOrder(workOrderId, saleNote?.id, resolvedCompanyId);
      return saleNote;
    } catch (err) {
      console.error('[saleNoteService] Failed to ensure sale note draft:', err);
      return null;
    }
  },

  async update(id, updateData) {
    try {
      const snakeData = toSnakeCase(updateData);
      const { data, error } = await supabase?.from('sale_notes')?.update(snakeData)?.eq('id', id)?.select()?.single();

      if (error) throw error;
      return toCamelCase(data);
    } catch (err) {
      console.error('[saleNoteService] Failed to update sale note:', err);
      throw err;
    }
  }
};

export const saleNoteItemService = {
  async getByNoteId(saleNoteId) {
    try {
      const { data, error } = await supabase?.from('sale_note_items')?.select('*')?.eq('sale_note_id', saleNoteId)?.order('sort_order');
      if (error) { console.error('[saleNoteItemService] Error:', error); return []; }
      return (data || [])?.map(toCamelCase);
    } catch (err) {
      console.error('[saleNoteItemService] Failed:', err);
      return [];
    }
  },

  async addItem(saleNoteId, companyId, itemData) {
    try {
      const qty = parseFloat(itemData?.quantity) || 1;
      const price = parseFloat(itemData?.unitPrice) || 0;
      const discPct = parseFloat(itemData?.discountPct) || 0;
      const taxPct = parseFloat(itemData?.taxPct) ?? 16;
      const subtotal = qty * price * (1 - discPct / 100);
      const total = subtotal * (1 + taxPct / 100);

      const payload = {
        sale_note_id: saleNoteId,
        company_id: companyId || null,
        item_type: itemData?.itemType || 'service',
        description: itemData?.description,
        quantity: qty,
        unit_price: price,
        discount_pct: discPct,
        tax_pct: taxPct,
        subtotal: parseFloat(subtotal?.toFixed(2)),
        total: parseFloat(total?.toFixed(2)),
        notes: itemData?.notes || null,
        sort_order: itemData?.sortOrder || 0
      };

      const { data, error } = await supabase?.from('sale_note_items')?.insert(payload)?.select()?.single();
      if (error) throw error;

      // Recalculate sale_note totals
      await saleNoteItemService?.recalculateTotals(saleNoteId);
      return toCamelCase(data);
    } catch (err) {
      console.error('[saleNoteItemService] Failed to add item:', err);
      throw err;
    }
  },

  async removeItem(itemId, saleNoteId) {
    try {
      const { error } = await supabase?.from('sale_note_items')?.delete()?.eq('id', itemId);
      if (error) throw error;
      if (saleNoteId) await saleNoteItemService?.recalculateTotals(saleNoteId);
    } catch (err) {
      console.error('[saleNoteItemService] Failed to remove item:', err);
      throw err;
    }
  },

  async recalculateTotals(saleNoteId) {
    try {
      const { data: items } = await supabase?.from('sale_note_items')?.select('subtotal, total')?.eq('sale_note_id', saleNoteId);
      const subtotal = (items || [])?.reduce((s, i) => s + (parseFloat(i?.subtotal) || 0), 0);
      const total = (items || [])?.reduce((s, i) => s + (parseFloat(i?.total) || 0), 0);
      const tax = total - subtotal;
      await supabase?.from('sale_notes')?.update({
        subtotal: parseFloat(subtotal?.toFixed(2)),
        tax: parseFloat(tax?.toFixed(2)),
        total: parseFloat(total?.toFixed(2)),
        updated_at: new Date()?.toISOString()
      })?.eq('id', saleNoteId);
    } catch (err) {
      console.error('[saleNoteItemService] Failed to recalculate totals:', err);
    }
  }
};
